import { BrowserSessionApiClient } from './browserSessionApiClient';

/*
 * FRONTEND DATA ACCESS LAYER
 * ===========================
 * This file is the bridge between the React screens and the backend API.
 *
 * The important idea is simple:
 * the browser never talks to SQL directly.
 * It calls `/api/...`, and the Azure Functions backend talks to the database for us.
 *
 * This is the active data path for the current app.
 * Older Dataverse/MSAL service files still exist in the repo, but this file
 * is the one normal screens should use today.
 */

function resolveApiBaseUrl() {
  const envValue = import.meta.env.VITE_API_BASE_URL;

  // Production SWA browser calls must stay same-origin so the SWA session cookie
  // and principal headers are available to the backend.
  if (import.meta.env.PROD) {
    return "/api";
  }

  if (envValue && envValue.trim() !== "") {
    return envValue.trim().replace(/\/+$/, "");
  }

  return "/api";
}

// Use `/api` by default so the browser stays on the same site and keeps the SWA session cookie.
const API_BASE_URL = (() => {
  return resolveApiBaseUrl();
})();

console.log('🔧 Azure SQL Service initialized with API_BASE_URL:', API_BASE_URL);

// This class keeps the app's data-loading rules in one place instead of spreading them across pages.
/*
 * MAIN AZURE SQL SERVICE CLASS (SWA VERSION)
 * ==========================================
 * This class provides all methods for accessing your business data
 * with rate limiting, retry logic, and error handling
 *
 * AUTHENTICATION: Uses SWA cookies (no tokens!)
 */
class AzureSqlService {
  constructor() {
    this.apiBaseUrl = API_BASE_URL;
    this.browserClient = new BrowserSessionApiClient(this.apiBaseUrl);
    // This is the app's idea of what each screen can do right now.
    this.entityCapabilities = Object.freeze({
      clients: Object.freeze({ read: true, create: false, update: false, delete: false }),
      projects: Object.freeze({ read: true, create: false, update: true, delete: true }),
      invoices: Object.freeze({ read: true, create: false, update: true, delete: true }),
      quotations: Object.freeze({ read: true, create: false, update: true, delete: true }),
      tasks: Object.freeze({ read: true, create: true, update: true, delete: true }),
      certificates: Object.freeze({ read: true, create: false, update: false, delete: false }),
      users: Object.freeze({ read: false, create: false, update: false, delete: false }),
      templates: Object.freeze({ read: false, create: false, update: false, delete: false }),
    });

    // If the network is flaky, retry a few times before giving up.
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      retryableStatusCodes: [429, 503, 502, 504, 0], // 0 = network error
    };

    // A few simple counters help with debugging slow or failing calls.
    this.requestMetrics = {
      totalRequests: 0,
      failedRequests: 0,
      rateLimitHits: 0,
      lastRequestTime: null,
    };

    // Keep recently loaded data in memory so repeated page visits feel faster.
    this.cache = new Map();
    this.cacheConfig = {
      defaultTtl: 5 * 60 * 1000, // 5 minutes
      maxCacheSize: 100,
    };
  }

  clearQuotationCaches() {
    // Quotation data is reused in several screens, so refresh all related cached copies together.
    this.clearCache('quotations');
    this.clearCache('pending_quotation_lookup_');
  }

  // Newer `/api/v1/*` endpoints return `{ success, data, meta }`.
  unwrapV1Envelope(response) {
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response;
  }

  // Older list-style endpoints may still return a plain array or a legacy `{ data, pagination }` shape.
  unwrapListResponse(response) {
    if (Array.isArray(response)) {
      return response;
    }

    if (response && typeof response === 'object' && Array.isArray(response.data)) {
      return response.data;
    }

    return response;
  }

  unwrapListEnvelope(response) {
    if (Array.isArray(response)) {
      return {
        data: response,
        pagination: null,
        query: null,
        correlationId: null,
      };
    }

    if (response && typeof response === 'object') {
      return {
        data: Array.isArray(response.data) ? response.data : [],
        pagination: response.pagination || response.meta?.pagination || null,
        query: response.query || response.meta?.query || null,
        correlationId: response.correlationId || response.meta?.correlationId || null,
      };
    }

    return {
      data: [],
      pagination: null,
      query: null,
      correlationId: null,
    };
  }

  /*
   * RETRY LOGIC WITH EXPONENTIAL BACKOFF
   * ====================================
   */
  // Wait a little longer after each failure so the server can recover.
  async sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  calculateRetryDelay(attempt, retryAfter = null) {
    if (retryAfter) {
      const retryAfterMs = parseInt(retryAfter) * 1000;
      return Math.min(retryAfterMs, this.retryConfig.maxDelay);
    }

    const exponentialDelay = this.retryConfig.baseDelay * Math.pow(2, attempt - 1);
    const jitter = Math.random() * 0.25 * exponentialDelay;

    return Math.min(exponentialDelay + jitter, this.retryConfig.maxDelay);
  }

  async executeWithRetry(requestFunction, operation = 'API request') {
    let lastError = null;

    for (let attempt = 1; attempt <= this.retryConfig.maxRetries + 1; attempt++) {
      try {
        this.requestMetrics.totalRequests++;
        this.requestMetrics.lastRequestTime = new Date();

        console.log(`🔄 ${operation} - Attempt ${attempt}/${this.retryConfig.maxRetries + 1}`);
        const result = await requestFunction();

        if (attempt > 1) {
          console.log(`✅ ${operation} succeeded after ${attempt} attempts`);
        }

        return result;

      } catch (error) {
        lastError = error;
        this.requestMetrics.failedRequests++;

        const isRetryableError = this.isRetryableError(error);
        const shouldRetry = attempt <= this.retryConfig.maxRetries && isRetryableError;

        console.log(`❌ ${operation} - Attempt ${attempt} failed:`, {
          error: error.message,
          status: error.status,
          isRetryable: isRetryableError,
          willRetry: shouldRetry
        });

        if (!shouldRetry) {
          if (error.status === 429) {
            this.requestMetrics.rateLimitHits++;
          }
          break;
        }

        const delay = this.calculateRetryDelay(attempt, error.retryAfter);
        console.log(`⏳ Waiting ${delay}ms before retry ${attempt + 1}...`);
        await this.sleep(delay);
      }
    }

    console.error(`🚫 ${operation} failed after ${this.retryConfig.maxRetries + 1} attempts:`, lastError);
    throw lastError;
  }

  ensureMethodSupported(entityName, method) {
    const key = (entityName || '').toLowerCase();
    const capabilities = this.entityCapabilities[key];

    if (!capabilities || !capabilities[method]) {
      // Pages should not try actions that the backend does not actually support.
      const error = new Error(
        `API contract mismatch: ${method.toUpperCase()} is not supported for ${entityName}. ` +
        `Current backend exposes GET-only endpoints for active entities.`
      );
      error.code = 'API_METHOD_NOT_SUPPORTED';
      error.status = 405;
      throw error;
    }
  }

  isRetryableError(error) {
    if (!error.status) return true; // Network errors
    return this.retryConfig.retryableStatusCodes.includes(error.status);
  }

  // The cache key needs to include filters and paging so different list views do not collide.
  serializeCacheSegment(value) {
    if (value === null || value === undefined || value === '') {
      return 'none';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    try {
      const seen = new WeakSet();
      return JSON.stringify(value, (_key, current) => {
        if (typeof current === 'function') {
          return `[function:${current.name || 'anonymous'}]`;
        }

        if (typeof current === 'object' && current !== null) {
          if (typeof window !== 'undefined' && current === window) {
            return '[window]';
          }

          if (typeof Event !== 'undefined' && current instanceof Event) {
            return {
              type: current.type,
              currentTarget: '[event-target]',
              target: '[event-target]',
            };
          }

          if (seen.has(current)) {
            return '[circular]';
          }
          seen.add(current);
        }

        return current;
      });
    } catch (error) {
      console.warn('Failed to serialize cache segment, falling back to object tag.', error);
      return Object.prototype.toString.call(value);
    }
  }

  getCacheKey(entity, options = {}) {
    const { page, pageSize, filter, sort, query } = options;
    const entityKey = String(entity || '').trim().toLowerCase() || 'entity';
    const queryKey = this.serializeCacheSegment(query);
    return `${entityKey}_${page || 1}_${pageSize || 10}_${filter || 'none'}_${sort || 'default'}_${queryKey}`;
  }

  getEntityRecordCacheKey(entity, id) {
    const entityKey = String(entity || '').trim().toLowerCase() || 'entity';
    return `${entityKey}_record_${String(id ?? '').trim()}`;
  }

  getFromCache(cacheKey) {
    const cached = this.cache.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      console.log(`📦 Cache HIT for ${cacheKey}`);
      return cached.data;
    }
    if (cached) {
      this.cache.delete(cacheKey);
      console.log(`⏰ Cache EXPIRED for ${cacheKey}`);
    }
    return null;
  }

  setCache(cacheKey, data, ttl = this.cacheConfig.defaultTtl) {
    if (this.cache.size >= this.cacheConfig.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(cacheKey, {
      data,
      expires: Date.now() + ttl,
      created: Date.now()
    });
    console.log(`💾 Cached ${cacheKey} for ${ttl}ms`);
  }

  clearCache(pattern = null) {
    if (pattern) {
      const normalizedPattern = String(pattern).toLowerCase();
      Array.from(this.cache.keys())
        .filter(key => String(key).toLowerCase().includes(normalizedPattern))
        .forEach(key => this.cache.delete(key));
      console.log(`🗑️ Cleared cache entries matching: ${pattern}`);
    } else {
      this.cache.clear();
      console.log('🗑️ Cleared all cache');
    }
  }

  /*
   * SWA COOKIE-BASED FETCH (NO TOKENS)
   * ===================================
   * Uses credentials: "include" to send authentication cookies
   * Backend receives user identity via x-ms-client-principal header
   */
  async apiFetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: "include", // Include SWA authentication cookies
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(options.headers || {}),
      },
    });
    const contentType = String(response.headers.get("Content-Type") || "").toLowerCase();
    const expectsJson = contentType.includes("application/json") || contentType.includes("+json");

    const readTextBody = async () => {
      try {
        return await response.text();
      } catch {
        return "";
      }
    };

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        if (retryAfter) {
          error.retryAfter = retryAfter;
          console.log(`🚦 Rate limited - Retry-After: ${retryAfter} seconds`);
        }
      }

      try {
        if (expectsJson) {
          const errorBody = await response.json();
          error.message = errorBody.error || errorBody.message || error.message;
          error.details = errorBody;
        } else {
          const textBody = await readTextBody();
          error.message = textBody.trim().startsWith("<!DOCTYPE") || textBody.trim().startsWith("<html")
            ? `API returned an HTML page instead of JSON for ${url}. Check the API route, deployment, and sign-in state.`
            : textBody.trim() || error.message;
          error.details = { contentType, bodyPreview: textBody.slice(0, 240) };
        }
      } catch {
        // Ignore if we can't parse or read the error body
      }

      throw error;
    }

    if (!expectsJson) {
      const textBody = await readTextBody();
      const error = new Error(
        textBody.trim().startsWith("<!DOCTYPE") || textBody.trim().startsWith("<html")
          ? `API returned an HTML page instead of JSON for ${url}. Check the API route, deployment, and sign-in state.`
          : `API returned ${contentType || "a non-JSON response"} for ${url}.`
      );
      error.status = response.status;
      error.details = { contentType, bodyPreview: textBody.slice(0, 240) };
      throw error;
    }

    return response.json();
  }

  /*
   * GENERIC ENTITY OPERATIONS
   * =========================
   */
  async getEntity(entityName, options = {}) {
    const { page = 1, pageSize = 10, filter = null, sort = null, query = null, useCache = true, returnEnvelope = false } = options;
    const operation = `get${entityName}(page=${page}, pageSize=${pageSize})`;

    // Check cache
    const cacheKey = this.getCacheKey(entityName, options);
    if (useCache) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) return cachedData;
    }

    return await this.executeWithRetry(async () => {
      const path = `${this.apiBaseUrl}/v1/${entityName.toLowerCase()}`;
      const response = await this.browserClient.request(path, {
        method: 'GET',
        query: {
          page,
          pageSize,
          ...(filter ? { filter } : {}),
          ...(sort ? { sort } : {}),
          ...(query && typeof query === 'object' ? query : {}),
        },
      });

      console.log(`✅ ${entityName} response:`, response?.data?.length || 0, 'records');

      const result = returnEnvelope
        ? this.unwrapListEnvelope(response)
        : this.unwrapListResponse(response);

      if (useCache) {
        this.setCache(cacheKey, result);
      }

      return result;
    }, operation);
  }

  async createEntity(entityName, data) {
    this.ensureMethodSupported(entityName, 'create');
    const operation = `create${entityName}`;

    return await this.executeWithRetry(async () => {
      const path = `${this.apiBaseUrl}/v1/${entityName.toLowerCase()}`;
      const result = await this.browserClient.request(path, {
        method: 'POST',
        body: data,
      });

      // Clear cache for this entity
      this.clearCache(entityName.toLowerCase());

      const payload = this.unwrapV1Envelope(result);
      return payload ? { ...payload, warnings: result?.meta?.warnings || [] } : result;
    }, operation);
  }

  async getEntityById(entityName, id, options = {}) {
    const { useCache = true } = options;
    const normalizedId = Number(id);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      const error = new Error(`${entityName} id must be a positive number`);
      error.code = 'ENTITY_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    const operation = `get${entityName}ById(${Math.trunc(normalizedId)})`;
    const cacheKey = this.getEntityRecordCacheKey(entityName, Math.trunc(normalizedId));
    if (useCache) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) return cachedData;
    }

    return await this.executeWithRetry(async () => {
      const path = `${this.apiBaseUrl}/v1/${entityName.toLowerCase()}/${Math.trunc(normalizedId)}`;
      const response = await this.browserClient.request(path, {
        method: 'GET',
      });

      const result = this.unwrapV1Envelope(response);
      if (useCache) {
        this.setCache(cacheKey, result);
      }

      return result;
    }, operation);
  }

  async getHomeDashboard() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/v1/dashboard/home`, {
        method: 'GET',
      });

      const payload = this.unwrapV1Envelope(response);
      return payload && typeof payload === 'object'
        ? {
            ...payload,
            warnings: Array.isArray(response?.meta?.warnings) ? response.meta.warnings : [],
            degraded: Boolean(response?.meta?.degraded),
          }
        : payload;
    }, 'dashboard:home');
  }

  async updateEntity(entityName, id, data) {
    this.ensureMethodSupported(entityName, 'update');
    const operation = `update${entityName}/${id}`;

    return await this.executeWithRetry(async () => {
      const path = `${this.apiBaseUrl}/v1/${entityName.toLowerCase()}/${id}`;
      const result = await this.browserClient.request(path, {
        method: 'PATCH',
        body: data,
      });

      // Clear cache for this entity
      this.clearCache(entityName.toLowerCase());

      return this.unwrapV1Envelope(result);
    }, operation);
  }

  async deleteEntity(entityName, id) {
    this.ensureMethodSupported(entityName, 'delete');
    const operation = `delete${entityName}/${id}`;

    return await this.executeWithRetry(async () => {
      const url = new URL(`${this.apiBaseUrl}/v1/${entityName.toLowerCase()}/${id}`, window.location.origin);

      console.log(`🗑️ Deleting ${entityName}/${id}`);

      const result = await this.browserClient.request(url.toString(), {
        method: 'DELETE',
      });

      console.log(`✅ ${entityName} deleted`);

      // Clear cache for this entity
      this.clearCache(entityName.toLowerCase());

      return this.unwrapV1Envelope(result) || { success: true };
    }, operation);
  }

  async postWorkflow(operation, data = {}, options = {}) {
    const op = String(operation || '').trim();
    if (!op) {
      const error = new Error('Workflow operation is required');
      error.code = 'WORKFLOW_OPERATION_REQUIRED';
      error.status = 400;
      throw error;
    }

    const idempotencyKey = `${op}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;

    const execute = async () => {
      const result = await this.browserClient.request(`${this.apiBaseUrl}/v1/commands/${encodeURIComponent(op)}`, {
        method: 'POST',
        body: data,
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      });

      // Workflow operations may affect multiple lists.
      this.clearQuotationCaches();
      this.clearCache('projects');
      this.clearCache('tasks');
      this.clearCache('invoices');
      this.clearCache('certificates');

      return result?.data || result;
    };
    return options.retry === false ? execute() : this.executeWithRetry(execute, `workflow:${op}`);
  }

  async createWorkflowQuotation(payload) {
    return this.postWorkflow('quotation.intake', payload);
  }

  async acceptWorkflowQuotation(payload) {
    return this.postWorkflow('quotation.accept', payload);
  }

  async createWorkflowProjectFromQuotation(payload) {
    return this.postWorkflow('quotation.create-project', payload);
  }

  async createWorkflowQuotationFolder(payload) {
    return this.postWorkflow('quotation.create-folder', payload);
  }

  async fileWorkflowQuotationPdf(payload) {
    return this.postWorkflow('quotation.file-pdf', payload);
  }

  async sendWorkflowQuotationEmail(payload) {
    return this.postWorkflow('quotation.send-email', payload);
  }

  async previewWorkflowQuotationEmail(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/workflows/quotation-send-email-preview`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return response?.data || response;
    }, 'quotationWorkflow:send-email-preview');
  }

  async issueQuotationSigningLink(payload) {
    return this.postWorkflow('quotation.issue-signing-link', payload);
  }

  async requestDocumentApproval(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/document-approvals/request`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return response?.data || response;
    }, 'documentApproval:request');
  }

  async verifyDocumentApproval(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/document-approvals/verify`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return response?.data || response;
    }, 'documentApproval:verify');
  }

  async provisionProjectFolders(projectId) {
    const normalizedProjectId = Number(projectId);
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      const error = new Error('A valid projectId is required to provision project folders');
      error.code = 'PROJECT_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    try {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/projects/${Math.trunc(normalizedProjectId)}/folders/provision`, {
        method: 'POST',
      });

      this.clearCache('projects');
      this.clearQuotationCaches();

      return response?.data || response;
    } catch (error) {
      // A timeout is not evidence of failed creation. Read the durable setup job;
      // never automatically replay a folder-creation POST after an unknown result.
      try {
        const status = await this.browserClient.request(`${this.apiBaseUrl}/projects/${Math.trunc(normalizedProjectId)}/folders/provision-status`);
        if (status?.data?.job) return { projectId: Math.trunc(normalizedProjectId), job: status.data.job,
          sharePoint: { integrationStatus: status.data.job.state === 'ready' ? 'configured' : 'queued', message: status.data.job.message } };
      } catch { /* Preserve the original failure as unconfirmed, not a repeated write. */ }
      if (!error?.status || Number(error.status) >= 500) return { projectId: Math.trunc(normalizedProjectId), unconfirmed: true,
        sharePoint: { integrationStatus: 'unconfirmed', message: 'Folder setup result could not be confirmed. Check setup status before retrying; this does not mean creation failed.' } };
      throw error;
    }
  }

  async refreshProjectFolderLink(projectId) {
    const normalizedProjectId = Number(projectId);
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      const error = new Error('A valid projectId is required to refresh the project folder link');
      error.code = 'PROJECT_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/projects/${Math.trunc(normalizedProjectId)}/folders/refresh`, {
        method: 'POST',
      });

      this.clearCache('projects');
      return response?.data || response;
    }, `project-folder-refresh:${Math.trunc(normalizedProjectId)}`);
  }

  async getWorkflowEngineerOptions() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/workflows/engineer-options`, {
        method: 'GET',
      });

      return response?.data || response;
    }, 'workflow:engineer-options');
  }

  async getWorkflowUserOptions() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/workflows/user-options`, {
        method: 'GET',
      });

      return response?.data || response;
    }, 'workflow:user-options');
  }

  async getCertificatePractitionerOptions() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/practitioner-options`, {
        method: 'GET',
      });

      return response?.data || response;
    }, 'certificate-workflow:practitioner-options');
  }

  async getCertificateBuildingSurveyorOptions() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/building-surveyor-options`, {
        method: 'GET',
      });

      return response?.data || response;
    }, 'certificate-workflow:building-surveyor-options');
  }

  async getCertificateBuildingClassificationOptions() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/building-classification-options`, {
        method: 'GET',
      });

      return response?.data || response;
    }, 'certificate-workflow:building-classification-options');
  }

  async getCertificateProvisionHistory(query = '') {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/provision-history?q=${encodeURIComponent(query)}`, {
        method: 'GET',
      });
      return response?.data || response;
    }, 'certificate-workflow:provision-history');
  }

  async saveCertificateBuildingClassificationOption(payload = {}) {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/building-classification-option`, {
        method: 'POST',
        body: payload,
      });

      return response?.data || response;
    }, 'certificate-workflow:building-classification-option');
  }

  async autoFillCertificateAustralianStandards(payload = {}) {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/australian-standards-autofill`, {
        method: 'POST',
        body: payload,
      });

      return response?.data || response;
    }, 'certificate-workflow:australian-standards-autofill');
  }

  async getWorkflowQuotationNumberStatus(quotationNumber = '') {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/workflows/quotation-number-status`, {
        method: 'GET',
        query: {
          quotationNumber: quotationNumber || '',
        },
      });

      return response?.data || response;
    }, 'workflow:quotation-number-status');
  }

  async getQuotationPaymentSchedule(payload = {}) {
    const query = {
      quotationNumber: payload.quotationNumber || '',
      structuralFee: payload.structuralFee ?? 0,
      civilFee: payload.civilFee ?? 0,
      soilReportFee: payload.soilReportFee ?? 0,
      structuralDepositPercentage: payload.structuralDepositPercentage ?? 10,
      structuralStage2Percentage: payload.structuralStage2Percentage ?? 70,
      civilDepositPercentage: payload.civilDepositPercentage ?? 10,
      civilStage2Percentage: payload.civilStage2Percentage ?? 70,
    };

    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/quotation-payment-schedule`, {
        method: 'GET',
        query,
      });

      return response?.data || response;
    }, 'quotation:payment-schedule');
  }

  async searchPendingQuotations(query, limit = 8) {
    const normalizedQuery = String(query || '').trim();
    if (normalizedQuery.length < 2) {
      return [];
    }

    const cacheKey = `pending_quotation_lookup_${normalizedQuery.toLowerCase()}_${limit}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/quotations`, {
        method: 'GET',
        query: {
          page: 1,
          pageSize: Math.max(1, Math.min(limit, 20)),
          q: normalizedQuery,
          pendingOnly: '1',
        },
      });

      const result = response?.data || [];
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'quotations:pending-lookup');
  }

  async searchSuburbs(query, limit = 8) {
    const normalizedQuery = String(query || '').trim();
    if (normalizedQuery.length < 2) {
      return [];
    }

    const cacheKey = `suburb_lookup_${normalizedQuery.toLowerCase()}_${limit}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/suburbs`, {
        method: 'GET',
        query: {
          q: normalizedQuery,
          limit,
        },
      });
      const result = data.data || [];
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'suburbs:lookup');
  }

  async lookupSuburb(suburb, limit = 8) {
    const normalizedSuburb = String(suburb || '').trim();
    if (normalizedSuburb.length < 2) {
      return [];
    }

    const cacheKey = `suburb_exact_${normalizedSuburb.toLowerCase()}_${limit}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/suburbs`, {
        method: 'GET',
        query: {
          suburb: normalizedSuburb,
          limit,
        },
      });
      const result = data.data || [];
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'suburbs:lookup-exact');
  }

  async lookupSuburbsByPostCode(postCode, limit = 20) {
    const digitsOnly = String(postCode || '').replace(/\D/g, '');
    if (!digitsOnly || digitsOnly.length < 3 || digitsOnly.length > 4) {
      return [];
    }

    const cacheKey = `suburb_postcode_${digitsOnly}_${limit}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/suburbs`, {
        method: 'GET',
        query: {
          postCode: digitsOnly,
          limit,
        },
      });
      const result = data.data || [];
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'suburbs:lookup-postcode');
  }

  async getQuotationExclusionOptions() {
    const cacheKey = 'quotation_exclusions';
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/quotation-exclusions`, {
        method: 'GET',
      });
      const result = data.data || [];
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'quotationExclusions:list');
  }

  async createQuotationExclusionOption(label) {
    const normalizedLabel = String(label || '').replace(/\s+/g, ' ').trim();
    if (!normalizedLabel) {
      const error = new Error('label is required');
      error.code = 'QUOTATION_EXCLUSION_LABEL_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/quotation-exclusions`, {
        method: 'POST',
        body: JSON.stringify({ label: normalizedLabel }),
      });
      this.clearCache('quotation_exclusions');
      this.clearCache('quotation_template_definition');
      return response?.data || response;
    }, 'quotationExclusions:create');
  }

  async deleteQuotationExclusionOption(label) {
    const normalizedLabel = String(label || '').replace(/\s+/g, ' ').trim();
    if (!normalizedLabel) {
      const error = new Error('label is required');
      error.code = 'QUOTATION_EXCLUSION_LABEL_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/quotation-exclusions`, {
        method: 'DELETE',
        body: JSON.stringify({ label: normalizedLabel }),
      });
      this.clearCache('quotation_exclusions');
      this.clearCache('quotation_template_definition');
      return response;
    }, 'quotationExclusions:delete');
  }

  async getQuotationScopeDetailLibrary() {
    const cacheKey = 'quotation_scope_detail_library';
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/quotation-scope-details`, {
        method: 'GET',
      });
      const result = data.data || data;
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'quotationScopeDetails:get');
  }

  async createQuotationScopeDetailItem(payload = {}) {
    const normalizedItemText = String(payload.itemText || '').replace(/\s+/g, ' ').trim();
    if (!normalizedItemText) {
      const error = new Error('itemText is required');
      error.code = 'QUOTATION_SCOPE_DETAIL_ITEM_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/quotation-scope-details`, {
        method: 'POST',
        body: JSON.stringify({
          discipline: payload.discipline,
          detailGroupKey: payload.detailGroupKey,
          detailGroupLabel: payload.detailGroupLabel,
          itemText: normalizedItemText,
        }),
      });
      this.clearCache('quotation_scope_detail_library');
      return response?.data || response;
    }, 'quotationScopeDetails:create');
  }

  async getQuotationPaymentStageRefLibrary() {
    const cacheKey = 'quotation_payment_stage_ref_library';
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/quotation-payment-stage-refs`, {
        method: 'GET',
      });
      const result = data.data || data;
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'quotationPaymentStageRefs:get');
  }

  async getQuotationTemplateDefinition() {
    const cacheKey = 'quotation_template_definition';
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/quotation-template`, {
        method: 'GET',
      });
      const result = data.data || data;
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, 'quotationTemplate:get');
  }

  async getInvoiceWorkflowCategories() {
    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/invoice-workflows/categories`, { method: 'GET' });
      return data.data || [];
    }, 'invoiceWorkflow:categories');
  }

  async upsertInvoiceWorkflowCategory(payload) {
    return await this.executeWithRetry(async () => {
      const url = new URL(`${this.apiBaseUrl}/invoice-workflows/category-upsert`, window.location.origin);
      const result = await this.apiFetch(url.toString(), {
        method: 'POST',
        body: JSON.stringify(payload || {})
      });
      this.clearCache('invoices');
      return result;
    }, 'invoiceWorkflow:category-upsert');
  }

  async createWorkflowInvoice(payload) {
    return await this.postWorkflow('invoice.create', payload || {});
  }

  async getWorkflowInvoiceNextNumber({ projectId = null, projectNumber = null } = {}) {
    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/invoice-workflows/next-number`, {
        method: 'GET',
        query: {
          ...(projectId !== null && projectId !== undefined && `${projectId}`.trim() !== '' ? { projectId: `${projectId}` } : {}),
          ...(projectNumber ? { projectNumber } : {}),
        },
      });
      return data.data || data;
    }, 'invoiceWorkflow:next-number');
  }

  async getWorkflowInvoiceDraft({ invoiceId = null, invoiceNumber = null } = {}) {
    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/invoice-workflows/draft`, {
        method: 'GET',
        query: {
          ...(invoiceId !== null && invoiceId !== undefined && `${invoiceId}`.trim() !== '' ? { invoiceId: `${invoiceId}` } : {}),
          ...(invoiceNumber ? { invoiceNumber } : {}),
        },
      });
      return data.data || data;
    }, 'invoiceWorkflow:draft');
  }

  async amendWorkflowInvoice(payload) {
    return await this.postWorkflow('invoice.amend', payload || {});
  }

  async linkWorkflowInvoiceDraftPdf(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/invoice-workflows/draft-pdf`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      this.clearCache('invoices');
      return response?.data || response;
    }, 'invoiceWorkflow:draft-pdf');
  }

  async sendLockWorkflowInvoice(payload) {
    return await this.postWorkflow('invoice.send-lock', payload || {});
  }

  async createInspectionInvoiceFolder(payload) {
    return await this.postWorkflow('invoice.inspection-folder', payload || {});
  }

  async createInspectionCalendarInvite(payload) {
    return await this.postWorkflow('invoice.inspection-calendar', payload || {});
  }

  async sendReminderWorkflowInvoice(payload) {
    // A lost provider response is unknown; a POST retry must not send again.
    return await this.postWorkflow('invoice.send-reminder', payload || {}, { retry: false });
  }

  async previewReminderWorkflowInvoiceEmail(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/invoice-workflows/reminder-preview`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return response?.data || response;
    }, 'invoiceWorkflow:reminder-preview');
  }

  async previewSendLockWorkflowInvoiceEmail(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/invoice-workflows/send-lock-preview`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      return response?.data || response;
    }, 'invoiceWorkflow:send-lock-preview');
  }

  async listDraftFiles(options = {}) {
    const {
      entityType = null,
      recordId = null,
      mine = true,
      ownerEmail = null,
      limit = 100,
    } = options;

    return await this.executeWithRetry(async () => {
      const result = await this.browserClient.request(`${this.apiBaseUrl}/v1/draft-files`, {
        method: 'GET',
        query: {
          ...(entityType ? { entityType } : {}),
          ...(recordId !== null && recordId !== undefined && `${recordId}`.trim() !== '' ? { recordId: `${recordId}` } : {}),
          mine: mine ? 'true' : 'false',
          ...(ownerEmail ? { ownerEmail } : {}),
          ...(limit ? { limit: `${limit}` } : {}),
        },
      });

      return result?.data || result;
    }, 'draftFiles:list');
  }

  async uploadDraftFile({ entityType, recordId = null, file, contentType = null, fileName = null }) {
    if (!entityType || `${entityType}`.trim() === '') {
      const error = new Error('entityType is required');
      error.code = 'DRAFT_ENTITY_TYPE_REQUIRED';
      error.status = 400;
      throw error;
    }

    if (!(file instanceof Blob)) {
      const error = new Error('file must be a Blob or File');
      error.code = 'DRAFT_FILE_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await fetch(`${this.apiBaseUrl}/v1/draft-files`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-draft-entity-type': `${entityType}`.trim().toLowerCase(),
          'x-draft-file-name': fileName || file.name || 'draft.bin',
          ...(recordId !== null && recordId !== undefined && `${recordId}`.trim() !== '' ? { 'x-draft-record-id': `${recordId}` } : {}),
          'Content-Type': contentType || file.type || 'application/octet-stream',
        },
        body: file,
      });

      const rawBody = await response.text();
      const body = rawBody ? JSON.parse(rawBody) : null;

      if (!response.ok) {
        const error = new Error(body?.error?.message || body?.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.details = body;
        throw error;
      }

      this.clearCache('draft-files');
      return body?.data || body;
    }, 'draftFiles:upload');
  }

  async deleteDraftFile(fileId) {
    if (!fileId || `${fileId}`.trim() === '') {
      const error = new Error('fileId is required');
      error.code = 'DRAFT_FILE_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const result = await this.browserClient.request(`${this.apiBaseUrl}/v1/draft-files`, {
        method: 'DELETE',
        query: {
          id: `${fileId}`.trim(),
        },
      });

      this.clearCache('draft-files');
      return result?.data || result;
    }, 'draftFiles:delete');
  }

  async downloadDraftFile(fileId) {
    if (!fileId || `${fileId}`.trim() === '') {
      const error = new Error('fileId is required');
      error.code = 'DRAFT_FILE_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await fetch(`${this.apiBaseUrl}/v1/draft-files/content?id=${encodeURIComponent(`${fileId}`.trim())}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        let body = null;
        try {
          body = await response.json();
        } catch {
          // Ignore JSON parse failures for binary endpoints.
        }
        const error = new Error(body?.error?.message || body?.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.details = body;
        throw error;
      }

      return {
        blob: await response.blob(),
        fileName: decodeURIComponent(response.headers.get('x-draft-file-name') || response.headers.get('content-disposition')?.split("filename*=UTF-8''")[1] || ''),
        contentType: response.headers.get('content-type') || 'application/octet-stream',
        entityType: response.headers.get('x-draft-entity-type') || null,
        recordId: response.headers.get('x-draft-record-id') || null,
        ownerEmail: response.headers.get('x-draft-owner-email') || null,
      };
    }, 'draftFiles:download');
  }

  async createWorkflowCertificate(payload) {
    return await this.postWorkflow('certificate.create', payload || {});
  }

  async getWorkflowCertificateDraft({ certificateId = null, certificateNumber = null } = {}) {
    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/draft`, {
        method: 'GET',
        query: {
          ...(certificateId !== null && certificateId !== undefined && `${certificateId}`.trim() !== '' ? { certificateId: `${certificateId}` } : {}),
          ...(certificateNumber ? { certificateNumber } : {}),
        },
      });
      return data.data || data;
    }, 'certificateWorkflow:draft');
  }

  async amendWorkflowCertificate(payload) {
    return await this.postWorkflow('certificate.amend', payload || {});
  }

  async renderWorkflowCertificate(payload) {
    return await this.postWorkflow('certificate.render', payload || {});
  }

  async linkWorkflowCertificateDraftPdf(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/certificate-workflows/draft-pdf`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      this.clearCache('certificates');
      return response?.data || response;
    }, 'certificateWorkflow:draft-pdf');
  }

  async saveWorkflowCertificateDraftPdfToSharePoint(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/certificate-workflows/draft-pdf-sharepoint`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      this.clearCache('certificates');
      return response?.data ? { ...response.data, workflow: response.workflow } : response;
    }, 'certificateWorkflow:draft-pdf-sharepoint');
  }

  async issueLockWorkflowCertificate(payload) {
    return await this.postWorkflow('certificate.issue-lock', payload || {});
  }

  async getCertificateWorkflowVersions(certificateId) {
    return await this.executeWithRetry(async () => {
      const data = await this.browserClient.request(`${this.apiBaseUrl}/certificate-workflows/versions`, {
        method: 'GET',
        query: { certificateId: String(certificateId) },
      });
      return data.data || [];
    }, 'certificateWorkflow:versions');
  }

  /*
   * SPECIFIC ENTITY METHODS
   * =======================
   */
  async getClients(options = {}) {
    return this.getEntity('Clients', options);
  }

  async listClients(options = {}) {
    const {
      page = 1,
      pageSize = 200,
      query = null,
      useCache = true,
      returnEnvelope = false,
    } = options;
    const operation = `listClients(page=${page}, pageSize=${pageSize})`;
    const cacheKey = this.getCacheKey('clients-list', { page, pageSize, query });

    if (useCache) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) return cachedData;
    }

    return await this.executeWithRetry(async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      if (query && typeof query === 'object') {
        Object.entries(query).forEach(([key, value]) => {
          if (value !== null && value !== undefined && `${value}`.trim() !== '') {
            params.set(key, String(value));
          }
        });
      }

      const response = await this.apiFetch(`${this.apiBaseUrl}/clients?${params.toString()}`);
      const result = returnEnvelope
        ? this.unwrapListEnvelope(response)
        : this.unwrapListResponse(response);

      if (useCache) {
        this.setCache(cacheKey, result);
      }

      return result;
    }, operation);
  }

  async saveClient(payload = {}) {
    return await this.executeWithRetry(async () => {
      const response = await this.apiFetch(`${this.apiBaseUrl}/clients`, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
      });
      this.clearCache('clients');
      return response?.data || response;
    }, 'clients:save');
  }

  async deleteClient(clientId) {
    const normalizedClientId = Number(clientId);
    if (!Number.isFinite(normalizedClientId) || normalizedClientId <= 0) {
      const error = new Error('A valid clientId is required to delete a client');
      error.code = 'CLIENT_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const params = new URLSearchParams();
      params.set('clientId', String(Math.trunc(normalizedClientId)));
      const response = await this.apiFetch(`${this.apiBaseUrl}/clients?${params.toString()}`, {
        method: 'DELETE',
      });
      this.clearCache('clients');
      return response?.data || response;
    }, `clients:delete:${Math.trunc(normalizedClientId)}`);
  }

  async searchClients(searchQuery, limit = 8) {
    const query = String(searchQuery || '').trim();
    if (query.length < 2) {
      return [];
    }

    return this.getClients({
      page: 1,
      pageSize: limit,
      query: { q: query },
      useCache: true,
    });
  }

  async getProjects(options = {}) {
    return this.getEntity('Projects', options);
  }

  async getProjectById(projectId, options = {}) {
    return this.getEntityById('Projects', projectId, options);
  }

  async updateProject(projectId, payload) {
    return this.updateEntity('Projects', projectId, payload);
  }

  async sendProjectCouncilEmail(projectId, payload = {}) {
    const normalizedProjectId = Number(projectId);
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
      const error = new Error('A valid projectId is required to send a council email');
      error.code = 'PROJECT_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/v1/projects/${Math.trunc(normalizedProjectId)}/council-email`, {
        method: 'POST',
        body: payload || {},
      });

      this.clearCache('projects');
      return this.unwrapV1Envelope(response);
    }, `projects:${Math.trunc(normalizedProjectId)}:council-email`);
  }

  async deleteProject(projectId) {
    return this.deleteEntity('Projects', projectId);
  }

  async getInvoices(options = {}) {
    return this.getEntity('Invoices', options);
  }

  async getInvoiceById(invoiceId, options = {}) {
    return this.getEntityById('Invoices', invoiceId, options);
  }

  async updateInvoice(invoiceId, payload) {
    return this.updateEntity('Invoices', invoiceId, payload);
  }

  async deleteInvoice(invoiceId, options = {}) {
    if (options && Object.prototype.hasOwnProperty.call(options, 'deleteSharePointFolder')) {
      return await this.executeWithRetry(async () => {
        const result = await this.browserClient.request(`${this.apiBaseUrl}/v1/invoices/${invoiceId}`, {
          method: 'DELETE',
          query: {
            deleteSharePointFolder: options.deleteSharePointFolder ? 'true' : 'false',
          },
        });

        this.clearCache('invoices');
        return this.unwrapV1Envelope(result) || { success: true };
      }, `deleteInvoices/${invoiceId}`);
    }

    return this.deleteEntity('Invoices', invoiceId);
  }

  async getQuotations(options = {}) {
    return this.getEntity('Quotations', options);
  }

  async getQuotationById(quotationId, options = {}) {
    return this.getEntityById('Quotations', quotationId, options);
  }

  async getQuotationSavedPdf(quotationId) {
    const normalizedQuotationId = Number(quotationId);
    if (!Number.isFinite(normalizedQuotationId) || normalizedQuotationId <= 0) {
      const error = new Error('A valid quotationId is required to view the saved quotation PDF');
      error.code = 'QUOTATION_ID_REQUIRED';
      error.status = 400;
      throw error;
    }

    return await this.executeWithRetry(async () => {
      const response = await fetch(`${this.apiBaseUrl}/v1/quotations/${Math.trunc(normalizedQuotationId)}/saved-pdf`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/pdf',
        },
      });

      if (!response.ok) {
        const contentType = String(response.headers.get('Content-Type') || '').toLowerCase();
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          if (contentType.includes('application/json')) {
            const payload = await response.json();
            message = payload?.error?.message || payload?.message || payload?.error || message;
          } else {
            message = (await response.text()).trim() || message;
          }
        } catch {
          // Keep the status-based fallback when the error response cannot be parsed.
        }

        const error = new Error(message);
        error.status = response.status;
        throw error;
      }

      return {
        blob: await response.blob(),
        contentDisposition: response.headers.get('Content-Disposition'),
      };
    }, `quotation:${Math.trunc(normalizedQuotationId)}:saved-pdf`);
  }

  async updateQuotation(quotationId, payload) {
    const result = await this.updateEntity('Quotations', quotationId, payload);
    this.clearQuotationCaches();
    return result;
  }

  async deleteQuotation(quotationId) {
    const result = await this.deleteEntity('Quotations', quotationId);
    this.clearQuotationCaches();
    return result;
  }

  async getTasks(options = {}) {
    return this.getEntity('Tasks', options);
  }

  async createTask(payload) {
    return this.createEntity('Tasks', payload);
  }

  async updateTask(taskId, payload) {
    return this.updateEntity('Tasks', taskId, payload);
  }

  async deleteTask(taskId) {
    return this.deleteEntity('Tasks', taskId);
  }

  async sendTaskReminder(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/task-daily-summary/remind`, {
        method: 'POST',
        body: payload || {},
      });
      return response?.data || response;
    }, 'tasks:send-reminder');
  }

  async previewNotificationEmail(payload) {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/v1/email-notifications/preview`, {
        method: 'POST',
        body: payload || {},
      });
      return this.unwrapV1Envelope(response);
    }, `email:preview:${payload?.entityType || 'unknown'}:${payload?.entityId || 'unknown'}`);
  }

  async sendNotificationEmail(payload) {
    // Graph may have accepted the message even if the HTTP response was lost.
    // Never automatically retry an external email side effect.
    const response = await this.browserClient.request(`${this.apiBaseUrl}/v1/email-notifications/send`, {
      method: 'POST',
      body: payload || {},
    });
    return this.unwrapV1Envelope(response);
  }

  async sendAutodeskOtpForwarderTrigger() {
    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/autodesk-otp-forwarder/send`, {
        method: 'POST',
      });
      return response?.data || response;
    }, 'admin:autodesk-otp-forwarder-send');
  }

  async searchProjects(searchQuery, limit = 8, options = {}) {
    const lifecycle = String(options?.lifecycle || '').trim();
    return this.getProjects({
      page: 1,
      pageSize: limit,
      query: { q: searchQuery, fast: '1', ...(lifecycle ? { lifecycle } : {}) },
      useCache: true,
    });
  }

  async matchProjectsFromDescription(description, limit = 5, options = {}) {
    const query = String(description || '').trim();
    const lifecycle = String(options?.lifecycle || '').trim();
    if (query.length < 4) {
      return [];
    }

    const cacheKey = `project_match_${query.toLowerCase()}_${limit}_${lifecycle || 'active'}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    return await this.executeWithRetry(async () => {
      const response = await this.browserClient.request(`${this.apiBaseUrl}/v1/projects/match`, {
        method: 'GET',
        query: {
          q: query,
          limit,
          ...(lifecycle ? { lifecycle } : {}),
        },
      });

      const result = response?.data || response;
      this.setCache(cacheKey, result, 60 * 1000);
      return result;
    }, `matchProjectsFromDescription(limit=${limit})`);
  }

  async getCertificates(options = {}) {
    return this.getEntity('Certificates', options);
  }

  async getUsers(options = {}) {
    return this.getEntity('Users', options);
  }

  async getTemplates(options = {}) {
    return this.getEntity('Templates', options);
  }

  /*
   * TEST CONNECTION
   * ==============
   */
  async testConnection() {
    const operation = 'testConnection (health check)';

    return await this.executeWithRetry(async () => {
      console.log('Testing Azure SQL Database connection...');
      // Build absolute URL from relative path
      const url = new URL(`${this.apiBaseUrl}/health`, window.location.origin);

      const data = await this.apiFetch(url.toString());

      console.log('Health check successful!', data);
      return { success: true, data };
    }, operation);
  }

  /*
   * REQUEST METRICS
   * ==============
   */
  getRequestMetrics() {
    return {
      ...this.requestMetrics,
      retryConfig: this.retryConfig
    };
  }

  getEntityCapabilities() {
    return this.entityCapabilities;
  }

  resetMetrics() {
    this.requestMetrics = {
      totalRequests: 0,
      failedRequests: 0,
      rateLimitHits: 0,
      lastRequestTime: null,
    };
  }
}

export const azureSqlService = new AzureSqlService();
